<template>
  <div class="view" v-if="canEdit" v-cloak>
    <EditCreateTitleBox :label="$t('works.parcel_management.name')" />

    <div class="parcel-management-container">
      <div class="left-box">
        <StepsBar :steps="steps" :current-step="currentStep" @step-clicked="handleStepClick" />

        <div class="parcel-management-step-content" :class="{ 'settings-step-content': currentStep === 1 }">
          <settings
            v-show="currentStep === 1"
            ref="settings"
            :form-data="formData"
            @update-form-data="updateFormData"
          />
          <dataGridTab
            v-show="currentStep === 2"
            ref="works"
            data-type="WORKS"
            :form-data="formData"
            @update-form-data="updateFormData"
          />
          <dataGridTab
            v-show="currentStep === 3"
            ref="consum"
            data-type="CONSUM"
            :form-data="formData"
            @update-form-data="updateFormData"
          />
          <dataGridTab
            v-show="currentStep === 4"
            ref="production"
            data-type="PRODUCTION"
            :form-data="formData"
            @update-form-data="updateFormData"
          />
          <summaryStep
            v-show="currentStep === 5"
            ref="summary"
            :form-data="formData"
            @update-form-data="updateFormData"
            @executed="onExecuted"
          />
        </div>

        <div class="parcel-management-footer">
          <button class="btn btn-outline-secondary" :disabled="currentStep === 1" @click="handleStepClick(currentStep - 1)">
            &larr; {{ $t("common.previous") }}
          </button>
          <button v-if="currentStep < steps.length" class="btn btn-primary" @click="handleStepClick(currentStep + 1)">
            {{ $t("common.next") }} &rarr;
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-else>
    <forbidden></forbidden>
  </div>
</template>

<script>
import StepsBar from "@/components/Common/StepsBar.vue";
import EditCreateTitleBox from "@/components/Common/EditCreateTitleBox.vue";
import Forbidden from "@/pages/errors/Forbidden";
import settings from "./partials/settings.vue";
import dataGridTab from "./partials/dataGridTab.vue";
import summaryStep from "./partials/summary.vue";
import { canEdit, permissions, permissionTypes } from "~/helpers/Permissions";
import { errorMessage } from "@/helpers/common";

export default {
  name: "ParcelManagement",
  components: {
    StepsBar,
    EditCreateTitleBox,
    Forbidden,
    settings,
    dataGridTab,
    summaryStep,
  },
  data() {
    return {
      currentStep: 1,
      steps: [
        { number: 1, component: "settings", label: this.$t("works.parcel_management.step_settings") },
        { number: 2, component: "works", label: this.$t("works.parcel_management.step_works") },
        { number: 3, component: "consum", label: this.$t("works.parcel_management.step_consum") },
        { number: 4, component: "production", label: this.$t("works.parcel_management.step_production") },
        { number: 5, component: "summary", label: this.$t("works.parcel_management.step_summary") },
      ],
      formData: {
        operation_type: null, // MERGE | SPLIT | TRANSFER
        source_parcels: [],
        target_parcels: [],
        data_types: {
          WORKS: true,
          CONSUM: true,
          PRODUCTION: true,
        },
        works_rows: [],
        consum_rows: [],
        production_rows: [],
        works_selected_row_uuids: [],
        consum_selected_row_uuids: [],
        production_selected_row_uuids: [],
      },
    };
  },
  computed: {
    config() {
      return {
        permission: permissions.PARCEL_MANAGEMENT,
        permission_type: permissionTypes.access,
      };
    },
    canEdit() {
      return canEdit(this);
    },
  },
  methods: {
    updateFormData(updatedData) {
      this.formData = { ...this.formData, ...updatedData };
    },
    handleStepClick(stepNumber) {
      if (stepNumber < 1 || stepNumber > this.steps.length) {
        return;
      }

      if (stepNumber > this.currentStep) {
        const currentStepComponent = this.$refs[this.steps[this.currentStep - 1].component];
        if (currentStepComponent && typeof currentStepComponent.validate === "function") {
          const valid = currentStepComponent.validate();
          if (valid?.error) {
            errorMessage(this, valid.message || this.$t("fill_out_error"));
            return;
          }
        }
      }

      this.currentStep = stepNumber;

      const nextStepComponent = this.$refs[this.steps[stepNumber - 1].component];
      if (nextStepComponent && typeof nextStepComponent.onEnter === "function") {
        nextStepComponent.onEnter();
      }
    },
    onExecuted() {
      this.$router.push({ name: "work-listing" });
    },
  },
};
</script>

<style scoped>
.view {
  height: calc(100% - 70px);
}

.parcel-management-container {
  display: flex;
  height: 100%;
  width: 100%;
}

.left-box {
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: transparent;
}

.parcel-management-step-content {
  flex: 1;
  min-height: 0;
  padding: 20px;
  overflow: hidden;
}

.settings-step-content {
  overflow-y: auto;
}

.parcel-management-footer {
  display: flex;
  justify-content: space-between;
  padding: 12px 20px;
  border-top: 1px solid #e0e0e0;
}
</style>
